import { useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { Controller, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { formatEnderecoCompleto } from '@dts/shared';
import { FormPage } from '../../shared/components/FormPage';
import { useCrudFormDialog } from '../../shared/api/useCrudFormDialog';
import { companiesHooks } from './hooks';
import { companyInputSchema, type CompanyFormValues } from './schemas';
import type { CompanyDto, CompanyInput } from './types';
import { useCompanyAddressesHooks } from './addresses/hooks';
import { CompanyAddressesDialog } from './addresses/CompanyAddressesDialog';
import { CompanyDocumentosFiscaisDialog } from './documentos-fiscais/CompanyDocumentosFiscaisDialog';

export interface CompanyFormDialogProps {
  open: boolean;
  company: CompanyDto | null;
  onClose: () => void;
}

const DEFAULT_VALUES: CompanyFormValues = {
  nome: '',
  documento: '',
  telefone: '',
  permiteApontamentoFuturo: false,
  permiteApontamentoAtrasado: false,
  diasUteisApontamentoAtrasado: null,
  enderecoComercialId: null,
  enderecoFaturamentoId: null,
  enderecoCorrespondenciaId: null,
  integracaoIntegraNotaAtiva: false,
  integracaoIntegraNotaTokenHomologacao: null,
  integracaoIntegraNotaTokenProducao: null,
  simplesNacional: false,
  ativo: true,
};

export function CompanyFormDialog({ open, company, onClose }: CompanyFormDialogProps): JSX.Element {
  const { t } = useTranslation();
  const [showTokenHomolog, setShowTokenHomolog] = useState(false);
  const [showTokenProd, setShowTokenProd] = useState(false);

  const { form, isEdit, onSubmit, serverError } = useCrudFormDialog<
    CompanyFormValues,
    CompanyDto,
    CompanyInput,
    number
  >({
    open,
    entity: company,
    schema: companyInputSchema,
    defaultValues: DEFAULT_VALUES,
    fromEntity: (c) => ({
      nome: c.nome,
      documento: c.documento,
      telefone: c.telefone,
      permiteApontamentoFuturo: c.permiteApontamentoFuturo,
      permiteApontamentoAtrasado: c.permiteApontamentoAtrasado,
      diasUteisApontamentoAtrasado: c.permiteApontamentoAtrasado
        ? c.diasUteisApontamentoAtrasado
        : null,
      enderecoComercialId: c.enderecoComercialId,
      enderecoFaturamentoId: c.enderecoFaturamentoId,
      enderecoCorrespondenciaId: c.enderecoCorrespondenciaId,
      integracaoIntegraNotaAtiva: c.integracaoIntegraNotaAtiva,
      integracaoIntegraNotaTokenHomologacao: c.integracaoIntegraNotaTokenHomologacao,
      integracaoIntegraNotaTokenProducao: c.integracaoIntegraNotaTokenProducao,
      simplesNacional: c.simplesNacional,
      ativo: c.ativo,
    }),
    toInput: (v) => ({
      ...v,
      diasUteisApontamentoAtrasado: v.permiteApontamentoAtrasado
        ? (v.diasUteisApontamentoAtrasado ?? null)
        : null,
    }),
    getId: (c) => c.id,
    hooks: { useCreate: companiesHooks.useCreate, useUpdate: companiesHooks.useUpdate },
    messages: {
      created: t('features.empresa.toasts.created'),
      updated: t('features.empresa.toasts.updated'),
    },
    focusField: 'nome',
    onClose,
  });

  const permiteAtrasado = useWatch({ control: form.control, name: 'permiteApontamentoAtrasado' });
  const integraAtiva = useWatch({ control: form.control, name: 'integracaoIntegraNotaAtiva' });

  const companyId = company?.id;
  const addressHooks = useCompanyAddressesHooks(companyId ?? 0);
  const addressListQuery = addressHooks.useList({ page: 1, pageSize: 100 });
  const availableAddresses = useMemo(
    () => (companyId ? (addressListQuery.data?.data ?? []) : []),
    [companyId, addressListQuery.data],
  );
  const [addressesDialogOpen, setAddressesDialogOpen] = useState(false);
  const [docsFiscaisDialogOpen, setDocsFiscaisDialogOpen] = useState(false);

  return (
    <FormPage
      title={isEdit ? t('features.empresa.editTitle') : t('features.empresa.createTitle')}
      form={form}
      onSubmit={onSubmit}
      onCancel={onClose}
      serverError={serverError}
    >
      <Stack spacing={2}>
        {/* Dados básicos */}
        <TextField
          label={t('features.empresa.fields.nome')}
          fullWidth
          inputProps={{ maxLength: 150 }}
          error={Boolean(form.formState.errors.nome)}
          helperText={form.formState.errors.nome?.message}
          {...form.register('nome')}
        />
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <TextField
            label={t('features.empresa.fields.documento')}
            fullWidth
            inputProps={{ maxLength: 18 }}
            error={Boolean(form.formState.errors.documento)}
            helperText={
              form.formState.errors.documento?.message ??
              t('features.empresa.fields.documentoHelper')
            }
            {...form.register('documento')}
          />
          <TextField
            label={t('features.empresa.fields.telefone')}
            fullWidth
            inputProps={{ maxLength: 20 }}
            error={Boolean(form.formState.errors.telefone)}
            helperText={form.formState.errors.telefone?.message}
            {...form.register('telefone')}
          />
        </Stack>

        <Divider textAlign="left" sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {t('features.empresa.sections.apontamentos')}
          </Typography>
        </Divider>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} flexWrap="wrap">
          <Controller
            control={form.control}
            name="permiteApontamentoFuturo"
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(field.value)}
                    onChange={(_, c) => field.onChange(c)}
                    onBlur={field.onBlur}
                    inputRef={field.ref}
                  />
                }
                label={t('features.empresa.fields.permiteApontamentoFuturo')}
              />
            )}
          />
          <Controller
            control={form.control}
            name="permiteApontamentoAtrasado"
            render={({ field }) => (
              <FormControlLabel
                control={
                  <Switch
                    checked={Boolean(field.value)}
                    onChange={(_, c) => field.onChange(c)}
                    onBlur={field.onBlur}
                    inputRef={field.ref}
                  />
                }
                label={t('features.empresa.fields.permiteApontamentoAtrasado')}
              />
            )}
          />
        </Stack>

        {permiteAtrasado && (
          <Controller
            control={form.control}
            name="diasUteisApontamentoAtrasado"
            render={({ field, fieldState }) => (
              <TextField
                type="number"
                label={t('features.empresa.fields.diasUteisApontamentoAtrasado')}
                helperText={
                  fieldState.error?.message ??
                  t('features.empresa.fields.diasUteisApontamentoAtrasadoHelper')
                }
                error={Boolean(fieldState.error)}
                inputProps={{ step: 1, min: 1, max: 999 }}
                value={field.value ?? ''}
                onChange={(e) =>
                  field.onChange(e.target.value === '' ? null : Number(e.target.value))
                }
                onBlur={field.onBlur}
                inputRef={field.ref}
                sx={{ maxWidth: 240 }}
              />
            )}
          />
        )}

        <Divider textAlign="left" sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {t('features.empresa.sections.enderecos')}
          </Typography>
        </Divider>

        {!companyId ? (
          <Alert severity="info" variant="outlined">
            {t('features.empresa.sections.enderecosHintCreate')}
          </Alert>
        ) : (
          <>
            <Stack
              direction={{ xs: 'column', sm: 'row' }}
              spacing={1}
              alignItems={{ sm: 'center' }}
              justifyContent="space-between"
            >
              <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                {t('features.empresa.sections.enderecosHint')}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<HomeWorkIcon />}
                onClick={() => setAddressesDialogOpen(true)}
              >
                {t('features.empresa.actions.manageAddresses')}
              </Button>
            </Stack>

            {(
              ['enderecoComercialId', 'enderecoFaturamentoId', 'enderecoCorrespondenciaId'] as const
            ).map((name) => (
              <Controller
                key={name}
                control={form.control}
                name={name}
                render={({ field, fieldState }) => (
                  <TextField
                    select
                    label={t(`features.empresa.fields.${name}`)}
                    helperText={fieldState.error?.message}
                    error={Boolean(fieldState.error)}
                    value={field.value ?? ''}
                    onChange={(e) =>
                      field.onChange(e.target.value === '' ? null : Number(e.target.value))
                    }
                    onBlur={field.onBlur}
                    inputRef={field.ref}
                    fullWidth
                  >
                    <MenuItem value="">{t('features.empresa.fields.enderecoSlotNone')}</MenuItem>
                    {availableAddresses.map((a) => (
                      <MenuItem key={a.id} value={a.id}>
                        {formatEnderecoCompleto({
                          logradouro: a.logradouro,
                          numero: a.numero,
                          complemento: a.complemento,
                          bairro: a.bairro,
                          cidade: a.cidade,
                          uf: a.uf,
                          cep: a.cep,
                        })}
                      </MenuItem>
                    ))}
                  </TextField>
                )}
              />
            ))}

            <CompanyAddressesDialog
              open={addressesDialogOpen}
              companyId={companyId}
              companyName={company?.nome}
              onClose={() => setAddressesDialogOpen(false)}
            />
          </>
        )}

        <Divider textAlign="left" sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {t('features.empresa.sections.documentosFiscais')}
          </Typography>
        </Divider>

        {!companyId ? (
          <Alert severity="info" variant="outlined">
            {t('features.empresa.sections.documentosFiscaisHintCreate')}
          </Alert>
        ) : (
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            alignItems={{ sm: 'center' }}
            justifyContent="space-between"
          >
            <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
              {t('features.empresa.sections.documentosFiscaisHint')}
            </Typography>
            <Button
              variant="outlined"
              size="small"
              startIcon={<ReceiptLongIcon />}
              onClick={() => setDocsFiscaisDialogOpen(true)}
            >
              {t('features.empresa.actions.manageDocumentosFiscais')}
            </Button>
          </Stack>
        )}

        {companyId !== undefined && (
          <CompanyDocumentosFiscaisDialog
            open={docsFiscaisDialogOpen}
            companyId={companyId}
            companyName={company?.nome}
            simplesNacional={company?.simplesNacional ?? false}
            onClose={() => setDocsFiscaisDialogOpen(false)}
          />
        )}

        <Divider textAlign="left" sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {t('features.empresa.sections.integracao')}
          </Typography>
        </Divider>

        <Controller
          control={form.control}
          name="simplesNacional"
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(field.value)}
                  onChange={(_, c) => field.onChange(c)}
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                />
              }
              label={t('features.empresa.fields.simplesNacional')}
            />
          )}
        />

        <Controller
          control={form.control}
          name="integracaoIntegraNotaAtiva"
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch
                  checked={Boolean(field.value)}
                  onChange={(_, c) => field.onChange(c)}
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                />
              }
              label={t('features.empresa.fields.integracaoIntegraNotaAtiva')}
            />
          )}
        />

        {integraAtiva && (
          <Stack spacing={2}>
            <TextField
              label={t('features.empresa.fields.integracaoIntegraNotaTokenHomologacao')}
              type={showTokenHomolog ? 'text' : 'password'}
              autoComplete="off"
              fullWidth
              inputProps={{ maxLength: 200 }}
              helperText={t('features.empresa.fields.tokenHelper')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      edge="end"
                      onClick={() => setShowTokenHomolog((s) => !s)}
                      aria-label={
                        showTokenHomolog
                          ? t('features.empresa.fields.hideToken')
                          : t('features.empresa.fields.showToken')
                      }
                    >
                      {showTokenHomolog ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              {...form.register('integracaoIntegraNotaTokenHomologacao')}
            />
            <TextField
              label={t('features.empresa.fields.integracaoIntegraNotaTokenProducao')}
              type={showTokenProd ? 'text' : 'password'}
              autoComplete="off"
              fullWidth
              inputProps={{ maxLength: 200 }}
              helperText={t('features.empresa.fields.tokenHelper')}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      edge="end"
                      onClick={() => setShowTokenProd((s) => !s)}
                      aria-label={
                        showTokenProd
                          ? t('features.empresa.fields.hideToken')
                          : t('features.empresa.fields.showToken')
                      }
                    >
                      {showTokenProd ? <VisibilityOffIcon /> : <VisibilityIcon />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
              {...form.register('integracaoIntegraNotaTokenProducao')}
            />
          </Stack>
        )}

        <Divider sx={{ mt: 1 }} />

        <Controller
          control={form.control}
          name="ativo"
          render={({ field }) => (
            <FormControlLabel
              control={
                <Switch
                  checked={field.value ?? true}
                  onChange={(_, c) => field.onChange(c)}
                  onBlur={field.onBlur}
                  inputRef={field.ref}
                />
              }
              label={t('features.empresa.fields.ativo')}
            />
          )}
        />
      </Stack>
    </FormPage>
  );
}
